import { RegistrationType, useCountExamEventReportItemSummaryQuery } from '@report/graphql';
import {
  Box,
  ChairAltOutlinedIcon,
  EventAvailableOutlinedIcon,
  Flex,
  GroupOutlinedIcon,
  HowToRegOutlinedIcon,
  NoAccountsOutlinedIcon,
  TotalSummaryCard,
} from '@shared/components';
import { formatNumber, toDayRange } from '@shared/utils';
import { useMemo } from 'react';

import { useExamEventOverviewReportSearchParams } from '../../hooks';

import { mapCountExamEventReportItemSummary } from './utils';

export default function ExamEventOverviewReportSummarySection() {
  const [{ startDate, endDate, registrationType, examType, examCenter, province }] =
    useExamEventOverviewReportSearchParams();

  const isSearchEmpty =
    !startDate &&
    !endDate &&
    !registrationType.length &&
    !examType.length &&
    !examCenter.length &&
    !province.length;

  const { startedAt, endedAt } = toDayRange(startDate, endDate);

  const { data, loading } = useCountExamEventReportItemSummaryQuery({
    variables: {
      countExamEventReportItemSummaryInput: {
        startedAt: startedAt as Date,
        endedAt: endedAt as Date,
        examCenterCodes: examCenter.includes('all') ? null : examCenter,
        registrationTypes: registrationType,
        examTypes: examType,
        provinceIds: province.includes('all') ? null : province,
      },
    },
    skip: isSearchEmpty,
  });

  const summariesCount = useMemo(() => {
    return mapCountExamEventReportItemSummary(
      data?.reportCountExamEventReportItemSummary.summariesCount,
    );
  }, [data?.reportCountExamEventReportItemSummary]);

  return (
    <Flex
      sx={(theme) => ({
        gap: theme.spacing(4),
        width: '100%',
        overflowX: 'scroll',
      })}
    >
      <Box sx={{ flex: 1, minWidth: '208px' }}>
        <TotalSummaryCard
          icon={<EventAvailableOutlinedIcon color='primary' fontSize='large' />}
          slotProps={{
            iconContainer: {
              sx: (theme) => ({ backgroundColor: theme.palette.primary.lighter }),
            },
            labelContainer: {
              sx: { minWidth: '125px' },
            },
          }}
          label='รอบสอบทั้งหมด'
          value={`${formatNumber(summariesCount.EVENT_COUNT.total)} รอบ`}
          loading={loading}
          statItems={[
            {
              label: 'Public',
              value: formatNumber(summariesCount.EVENT_COUNT[RegistrationType.Public]),
            },
            {
              label: 'In-house',
              value: formatNumber(summariesCount.EVENT_COUNT[RegistrationType.InHouse]),
            },
          ]}
        />
      </Box>

      <Box sx={{ flex: 1, minWidth: '208px' }}>
        <TotalSummaryCard
          icon={<ChairAltOutlinedIcon sx={{ color: 'hsla(262, 52%, 47%, 1)' }} fontSize='large' />}
          slotProps={{
            iconContainer: {
              sx: { backgroundColor: 'hsla(264, 45%, 94%, 1)' },
            },
            labelContainer: {
              sx: { minWidth: '125px' },
            },
          }}
          label='จำนวนเปิดรับสมัคร'
          value={`${formatNumber(summariesCount.SEAT_COUNT.total)} ที่นั่ง`}
          loading={loading}
          statItems={[
            {
              label: 'Public',
              value: formatNumber(summariesCount.SEAT_COUNT[RegistrationType.Public]),
            },
            {
              label: 'In-house',
              value: formatNumber(summariesCount.SEAT_COUNT[RegistrationType.InHouse]),
            },
          ]}
        />
      </Box>

      <Box sx={{ flex: 1, minWidth: '208px' }}>
        <TotalSummaryCard
          icon={<GroupOutlinedIcon color='info' fontSize='large' />}
          slotProps={{
            iconContainer: {
              sx: (theme) => ({ backgroundColor: theme.palette.info.lighter }),
            },
            labelContainer: {
              sx: { minWidth: '125px' },
            },
          }}
          label='จำนวนผู้สมัครสอบ'
          value={`${formatNumber(summariesCount.REGISTERED_EXAMINEE_COUNT.total)} คน`}
          loading={loading}
          statItems={[
            {
              label: 'Public',
              value: formatNumber(
                summariesCount.REGISTERED_EXAMINEE_COUNT[RegistrationType.Public],
              ),
            },
            {
              label: 'In-house',
              value: formatNumber(
                summariesCount.REGISTERED_EXAMINEE_COUNT[RegistrationType.InHouse],
              ),
            },
          ]}
        />
      </Box>

      <Box sx={{ flex: 1, minWidth: '208px' }}>
        <TotalSummaryCard
          icon={<HowToRegOutlinedIcon color='success' fontSize='large' />}
          slotProps={{
            iconContainer: {
              sx: (theme) => ({ backgroundColor: theme.palette.success.lighter }),
            },
            labelContainer: {
              sx: { minWidth: '125px' },
            },
          }}
          label='จำนวนผู้เข้าสอบ'
          value={`${formatNumber(summariesCount.ATTENDED_EXAMINEE_COUNT.total)} คน`}
          loading={loading}
          statItems={[
            {
              label: 'Public',
              value: formatNumber(summariesCount.ATTENDED_EXAMINEE_COUNT[RegistrationType.Public]),
            },
            {
              label: 'In-house',
              value: formatNumber(summariesCount.ATTENDED_EXAMINEE_COUNT[RegistrationType.InHouse]),
            },
          ]}
        />
      </Box>

      <Box sx={{ flex: 1, minWidth: '208px' }}>
        <TotalSummaryCard
          icon={<NoAccountsOutlinedIcon color='error' fontSize='large' />}
          slotProps={{
            iconContainer: {
              sx: (theme) => ({ backgroundColor: theme.palette.error.lighter }),
            },
            labelContainer: {
              sx: { minWidth: '125px' },
            },
          }}
          label='จำนวนผู้ขาดสอบ'
          value={`${formatNumber(summariesCount.ABSENT_EXAMINEE_COUNT.total)} คน`}
          loading={loading}
          statItems={[
            {
              label: 'Public',
              value: formatNumber(summariesCount.ABSENT_EXAMINEE_COUNT[RegistrationType.Public]),
            },
            {
              label: 'In-house',
              value: formatNumber(summariesCount.ABSENT_EXAMINEE_COUNT[RegistrationType.InHouse]),
            },
          ]}
        />
      </Box>
    </Flex>
  );
}
